import React, { useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  AsyncAutocompleteCandidate,
  AsyncClientSelect,
} from "components/Common/AsyncSearchSelects";
import StatusMetadataBadge from "components/Common/StatusMetadataBadge";
import {
  buildCandidateAsyncFilterInitialOption,
  buildClientAsyncFilterInitialOption,
} from "helpers/metadataFilterHelpers";
import { statusType } from "helpers/GeneralHelper";
import { normalizeCandidateStatusMetadataValue } from "helpers/statusMetadataHelpers";
import {
  uploadCandidateDrugCsv,
  verifyCandidateDrugCsv,
} from "model/CandidateModel";

const drugCsvUploadSchema = Yup.object().shape({
  clientId: Yup.string().trim().required("Please select a client"),
  file: Yup.mixed()
    .required("Please choose a CSV file")
    .test("is-csv", "Only CSV files are allowed", (value) => {
      if (!value || !(value instanceof File)) return false;
      const name = String(value.name || "").toLowerCase();
      return (
        name.endsWith(".csv") ||
        value.type === "text/csv" ||
        value.type === "application/vnd.ms-excel"
      );
    }),
});

const isCsvFile = (file) => {
  if (!file || !(file instanceof File)) return false;
  const name = String(file.name || "").toLowerCase();
  return (
    name.endsWith(".csv") ||
    file.type === "text/csv" ||
    file.type === "application/vnd.ms-excel"
  );
};

const unwrapDrugCsvUploadRows = (response) => {
  const root = response?.data ?? response ?? {};
  const rows =
    root?.rows ??
    root?.items ??
    root?.records ??
    (Array.isArray(root) ? root : []);
  return Array.isArray(rows) ? rows : [];
};

const normalizeDrugCsvPreviewRow = (row = {}, index = 0) => ({
  firstName: row?.firstName ?? "",
  lastName: row?.lastName ?? "",
  governmentID: row?.governmentID ?? "",
  orderID: row?.orderID ?? row?.orderId ?? "",
  reportStatus: row?.reportStatus ?? "",
  reportType: row?.reportType ?? "",
  orderStatus: row?.orderStatus ?? "",
  drugStatus: row?.drugStatus ?? "",
  mecStatus: row?.mecStatus ?? "",
  orderStatusHtml: row?.orderStatusHtml ?? "",
  drugStatusHtml: row?.drugStatusHtml ?? "",
  mecStatusHtml: row?.mecStatusHtml ?? "",
  requestor: row?.requestor ?? "",
  clientId: row?.clientId ?? "",
  candidateId: row?.candidateId ?? "",
  candidateName: row?.candidateName ?? "",
  candidateIdLabel: row?.candidateName ?? row?.candidateIdLabel ?? "",
  rowCss: row?.rowCss ?? "",
  rowData: row?.rowData ?? "",
  testResult: row?.testResult ?? "",
  testDate: row?.testDate ?? "",
  _rowKey: `${row?.orderID ?? row?.orderId ?? "row"}-${index}`,
});

const buildVerifyItemPayload = (row = {}, submitClientId = "") => ({
  firstName: String(row?.firstName ?? ""),
  lastName: String(row?.lastName ?? ""),
  governmentID: String(row?.governmentID ?? ""),
  orderID: String(row?.orderID ?? row?.orderId ?? ""),
  reportStatus: String(row?.reportStatus ?? ""),
  reportType: String(row?.reportType ?? ""),
  requestor: String(row?.requestor ?? ""),
  orderStatus: String(row?.orderStatus ?? ""),
  drugStatus: String(row?.drugStatus ?? ""),
  mecStatus: String(row?.mecStatus ?? ""),
  orderStatusHtml: String(row?.orderStatusHtml ?? ""),
  drugStatusHtml: String(row?.drugStatusHtml ?? ""),
  mecStatusHtml: String(row?.mecStatusHtml ?? ""),
  clientId: String(row?.clientId ?? submitClientId ?? ""),
  candidateId: String(row?.candidateId ?? ""),
  candidateName: String(row?.candidateName ?? row?.candidateIdLabel ?? "").trim(),
  rowCss: String(row?.rowCss ?? ""),
  rowData: String(row?.rowData ?? ""),
  testResult: String(row?.testResult ?? ""),
  testDate: String(row?.testDate ?? ""),
});

/** Status pills — same `StatusMetadataBadge` + `statusType` fallback as CandidateTableContainer. */
const DrugCsvStatusCell = ({ accessor, value }) => (
  <StatusMetadataBadge
    accessor={accessor}
    pageName="Candidates"
    value={normalizeCandidateStatusMetadataValue(accessor, value)}
    fallbackClassName={statusType(String(value ?? ""))}
  />
);

const UploadDrugCSV = () => {
  const fileInputRef = useRef(null);
  const [clientId, setClientId] = useState("");
  const [clientIdLabel, setClientIdLabel] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [uploadOrderId, setUploadOrderId] = useState("");
  /** Client id from AsyncClientSelect at upload time (used for verify API). */
  const [uploadClientId, setUploadClientId] = useState(null);

  const showPreview = previewRows.length > 0;

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (file && !isCsvFile(file)) {
      setSelectedFile(null);
      setErrors((prev) => ({
        ...prev,
        file: "Only CSV files are allowed",
      }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
    if (errors.file) {
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const resetUploadForm = () => {
    setClientId("");
    setClientIdLabel("");
    setSelectedFile(null);
    setErrors({});
    setPreviewRows([]);
    setUploadOrderId("");
    setUploadClientId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearPreview = () => {
    setPreviewRows([]);
    setUploadOrderId("");
    setUploadClientId(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCandidateChange = (rowKey, option) => {
    setPreviewRows((prev) =>
      prev.map((row) =>
        row._rowKey === rowKey
          ? {
              ...row,
              candidateId: String(option?.value ?? "").trim(),
              candidateIdLabel: String(option?.label ?? "").trim(),
              candidateName: String(option?.label ?? "").trim(),
            }
          : row
      )
    );
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    try {
      await drugCsvUploadSchema.validate(
        {
          clientId: String(clientId ?? "").trim(),
          file: selectedFile,
        },
        { abortEarly: false }
      );
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const selectedClientId = String(clientId ?? "".trim());
    const params = new FormData();
    params.append("ClientId", String(selectedClientId));
    params.append("File", selectedFile);

    setLoading(true);
    const response = await uploadCandidateDrugCsv(params);
    setLoading(false);

    if (response?.success) {
      toast.success(response?.message);
      const rows = unwrapDrugCsvUploadRows(response).map(normalizeDrugCsvPreviewRow);
      if (!rows.length) {
        toast.warning("Upload succeeded but no rows were returned to review.");
        return;
      }
      const root = response?.data ?? response ?? {};
      const orderId =
        String(root?.orderId ?? root?.orderID ?? rows[0]?.orderID ?? "").trim();
      setUploadClientId(selectedClientId);
      setUploadOrderId(orderId);
      setPreviewRows(rows);
    } else {
      toast.error(response?.message);
    }
  };

  const handleConfirm = async () => {
    const selectedClientId = uploadClientId;
    if (!selectedClientId) {
      toast.error("Please select a client before confirming");
      return;
    }

    const swalResult = await Swal.fire({
      title: "Confirm verification?",
      text: "Are you sure you want to verify these drug CSV records?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;

    const payload = {
      orderId: String(uploadOrderId || previewRows[0]?.orderID || "").trim(),
      clientId: String(selectedClientId).trim(),
      items: previewRows.map((row) => buildVerifyItemPayload(row, selectedClientId)),
    };

    setVerifying(true);
    const response = await verifyCandidateDrugCsv(payload);
    setVerifying(false);

    if (response?.success) {
      toast.success(response?.message);
      resetUploadForm();
    } else {
      toast.error(response?.message);
    }
  };

  return (
    <div className="page-content upload-drug-csv-page">
      <Container fluid>
        <div className="upload-drug-csv-header mb-4">
          <div className="upload-drug-csv-header-icon">
            <i className="mdi mdi-file-upload-outline" aria-hidden />
          </div>
          <div>
            <h4 className="fw-bold mb-1">Upload Drug CSV</h4>
            <p className="text-muted mb-0">
              Select a client and upload a CSV file to import drug screening data.
            </p>
          </div>
        </div>

        <Card className="upload-drug-csv-panel border-0 shadow-sm">
          <CardBody className="p-4">
            <Form onSubmit={handleUpload}>
              <Row className="g-3 upload-drug-csv-form-row">
                <Col xs="12" lg="4">
                  <div className="upload-drug-csv-field">
                    <Label className="upload-drug-csv-label fw-semibold">
                      Client <span className="text-danger">*</span>
                    </Label>
                    <div
                      className={`upload-drug-csv-control${
                        errors.clientId ? " is-invalid" : ""
                      }`}
                    >
                      <AsyncClientSelect
                        key={`upload-drug-csv-client-${clientId}`}
                        pageName="CandidateFormRestrict"
                        value={clientId}
                        initialOption={buildClientAsyncFilterInitialOption(clientId, clientIdLabel)}
                        onChange={(option) => {
                          setClientId(String(option?.value ?? "").trim());
                          setClientIdLabel(String(option?.label ?? "").trim());
                          if (errors.clientId) {
                            setErrors((prev) => ({ ...prev, clientId: "" }));
                          }
                        }}
                        placeholder="Select Client"
                        isDisabled={showPreview}
                      />
                    </div>
                    {errors.clientId ? (
                      <FormFeedback className="d-block mt-1">
                        {errors.clientId}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>

                <Col xs="12" lg="5">
                  <div className="upload-drug-csv-field">
                    <Label className="upload-drug-csv-label fw-semibold">
                      CSV file <span className="text-danger">*</span>
                    </Label>
                    <div
                      className={`upload-drug-csv-control${
                        errors.file ? " is-invalid" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className={`upload-drug-csv-dropzone w-100${
                          errors.file ? " upload-drug-csv-dropzone--invalid" : ""
                        }${selectedFile ? " upload-drug-csv-dropzone--has-file" : ""}`}
                        onClick={openFilePicker}
                        disabled={loading || verifying || showPreview}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv,text/csv,application/vnd.ms-excel"
                          className="d-none"
                          onChange={handleFileChange}
                        />
                        <span className="upload-drug-csv-dropzone-icon" aria-hidden>
                          <i className="mdi mdi-file-delimited-outline" />
                        </span>
                        <span className="upload-drug-csv-dropzone-text text-truncate">
                          {selectedFile ? (
                            <span className="upload-drug-csv-dropzone-filename">
                              {selectedFile.name}
                            </span>
                          ) : (
                            <span className="upload-drug-csv-dropzone-placeholder">
                              <span className="fw-semibold">Choose a CSV file</span>
                              <span className="text-muted"> · .csv only</span>
                            </span>
                          )}
                        </span>
                      </button>
                    </div>
                    {errors.file ? (
                      <FormFeedback className="d-block mt-1">{errors.file}</FormFeedback>
                    ) : null}
                  </div>
                </Col>

                <Col xs="12" lg="3">
                  <div className="upload-drug-csv-field">
                    <Label
                      className="upload-drug-csv-label upload-drug-csv-label--spacer fw-semibold"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </Label>
                    <div className="upload-drug-csv-control">
                      <Button
                        color="primary"
                        type="submit"
                        className="upload-drug-csv-submit-btn w-100"
                        disabled={loading || verifying || showPreview}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-cloud-upload-outline me-2" aria-hidden />
                            Upload
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {showPreview ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="fw-bold mb-1">Review uploaded records</h5>
                  <p className="text-muted mb-0 small">
                    {previewRows.length} record{previewRows.length === 1 ? "" : "s"}
                    {uploadClientId ? (
                      <>
                        {" "}
                        · Client ID: <span className="fw-semibold">{uploadClientId}</span>
                      </>
                    ) : null}
                    {/* {uploadOrderId ? (
                      <>
                        {" "}
                        · Order ID: <span className="fw-semibold">{uploadOrderId}</span>
                      </>
                    ) : null} */}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="light"
                    className="border"
                    type="button"
                    onClick={clearPreview}
                    disabled={verifying}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    type="button"
                    onClick={handleConfirm}
                    disabled={verifying}
                  >
                    {verifying ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Confirming…
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-check-circle-outline me-1" aria-hidden />
                        Confirm
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="table-responsive candidate-csv-preview-table-wrap">
                <Table className="candidate-csv-preview-table align-middle mb-0" hover>
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: "48px" }}>
                        SN
                      </th>
                      <th>First name</th>
                      <th>Last name</th>
                      <th>Report status</th>
                      <th>Report type</th>
                      <th>Order status</th>
                      <th>Drug status</th>
                      <th>MEC status</th>
                      <th>Requestor</th>
                      <th style={{ minWidth: "180px" }}>Candidate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, index) => (
                      <tr key={row._rowKey}>
                        <td className="text-center text-muted">{index + 1}</td>
                        <td>{row.firstName || "—"}</td>
                        <td>{row.lastName || "—"}</td>
                        <td>{row.reportStatus || "—"}</td>
                        <td>{row.reportType || "—"}</td>
                        <td>
                          <DrugCsvStatusCell accessor="orderStatus" value={row.orderStatus} />
                        </td>
                        <td>
                          <DrugCsvStatusCell accessor="drugStatus" value={row.drugStatus} />
                        </td>
                        <td>
                          <DrugCsvStatusCell accessor="mecStatus" value={row.mecStatus} />
                        </td>
                        <td>{row.requestor || "—"}</td>
                        <td>
                          <AsyncAutocompleteCandidate
                            clientId={uploadClientId}
                            value={row.candidateId}
                            initialOption={buildCandidateAsyncFilterInitialOption(
                              row.candidateId,
                              row.candidateIdLabel
                            )}
                            onChange={(option) =>
                              handleCandidateChange(row._rowKey, option)
                            }
                            placeholder="Select candidate"
                            isDisabled={verifying}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        ) : null}
      </Container>
    </div>
  );
};

export default UploadDrugCSV;
